// Human labels for Sentinel rule ids. The server sends a per-issue human `title`, but
// the FE also prints rule_id directly in a few spots that have no loaded issue to borrow
// a title from (the faceted issue-type filter) or where a compact type tag is wanted
// (the alert row + evidence heading). ruleLabel is the single source for those labels,
// with a fallback that prettifies any unknown id so a newly-added server rule never
// renders as a raw slug.

const RULE_LABELS: Record<string, string> = {
  E8_segment_file_missing: 'Custom segment file missing in storage',
  io_under_pace: 'IO under pacing',
  io_over_pace: 'IO over pacing',
  io_delivery_drop: 'IO delivery drop',
  io_delivery_spike: 'IO delivery spike',
  under_pace: 'Under pacing',
  over_pace: 'Over pacing',
  no_delivery: 'No delivery',
  delivery_drop: 'Delivery drop',
  delivery_spike: 'Delivery spike',
};

// Sentence-cases a snake_case fragment: "segment_file_missing" -> "Segment file missing".
function prettify(fragment: string): string {
  const words = fragment.split('_').filter(Boolean).join(' ');
  if (!words) {
    return fragment;
  }
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
}

// Taxonomy codes are a letter-block + digits prefix on a snake_case body, e.g.
// "E8_segment_file_missing" or "W3_pacing_behind". The opaque prefix is dropped from the
// label (the raw id is still shown demoted for debugging) and surfaced as "Taxonomy: …".
const TAXONOMY_CODE = /^[A-Za-z]+\d+_(.+)$/;

// Case-folds a label for redundancy comparison: the "Taxonomy: " prefix and all
// punctuation are dropped and the "IO" abbreviation is expanded, so a chip reading
// "IO under pacing" compares equal to a title reading "Insertion order under-pacing".
function normalizeLabel(text: string): string {
  return text
    .toLowerCase()
    .replace(/^taxonomy:\s*/, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\bio\b/g, 'insertion order')
    .trim();
}

// chipRedundant reports whether a rule chip would only repeat the issue title. The raw
// rule id stays reachable through the row's title tooltip and the inspector.
export function chipRedundant(chipLabel: string, title: string): boolean {
  const chip = normalizeLabel(chipLabel);
  return chip !== '' && normalizeLabel(title).includes(chip);
}

// ruleLabel returns a human label for a Sentinel rule id, falling back to a prettified
// form for ids not in the map so no user-facing surface ever renders a raw slug.
export default function ruleLabel(ruleId: string): string {
  if (RULE_LABELS[ruleId]) {
    return RULE_LABELS[ruleId];
  }
  const taxonomy = TAXONOMY_CODE.exec(ruleId);
  if (taxonomy) {
    return `Taxonomy: ${prettify(taxonomy[1])}`;
  }
  return prettify(ruleId);
}
